/**
 * AdjustLastOperationPanel — Blender-style F9 redo panel.
 *
 * After a parameterized operation (bevel, inset, loop cut, shrink/fatten)
 * is confirmed, this floating panel appears in the bottom-left of the
 * viewport. Adjusting any parameter re-executes the operation from the
 * pre-operation snapshot — no new undo entry is created.
 *
 * The panel is driven entirely by command metadata — no
 * per-operator UI code is needed.
 */

import type { CSSProperties } from "react";
import { useCallback, useEffect, useMemo } from "react";
import { useStore } from "../app/store.ts";
import { commandSnapshotObject } from "../app/storeUndo.ts";
import { FALLBACK_DIAGONAL, objectDiagonal } from "../modeling/objectBounds.ts";
import {
  getCommandMetadata,
  type ParamDef,
  resolveFloatRange,
} from "../modeling/operatorParams.ts";
import { DraggablePanel } from "./DraggablePanel.tsx";
import { usePanelSelection } from "./floatingPanelStore.ts";
import { getPanelBodyStyle } from "./panelSurface.tsx";
import {
  FoldoutSection,
  SegmentedControl,
  SliderRow,
  Toggle,
} from "./shared.tsx";
import {
  COLOR_ACCENT,
  COLOR_TEXT_DIM,
  FONT_SIZE_BODY,
  FONT_SIZE_SMALL,
  Z_FLOATING,
} from "./tokens.ts";

const PANEL_ID = "adjust-last-operation";

const PANEL_BODY: CSSProperties = getPanelBodyStyle({
  padding: "8px 12px",
  maxWidth: "min(240px, calc(100vw - 16px))",
});

export function AdjustLastOperationPanel() {
  const lastOp = useStore((s) => s.lastOperation);
  // Reference length for `scaledByBounds` slider ranges. Different replay
  // kinds source this differently:
  //
  //   - geometry-snapshot (extrude / bevel / inset / …): the PRE-op
  //     snapshot's diagonal. The live object reflects the op's current
  //     output — extruding a face grows the mesh, and reading the live bbox
  //     would let the slider max chase its own value upward into nonsense
  //     (10k+ meters). The snapshot is frozen at confirm time, so the
  //     reference is stable throughout the redo-panel session.
  //
  //   - selection-seed: never opts into `scaledByBounds` (params are unitless
  //     degrees / counts), so the fallback is fine.
  const diagonal = useMemo(() => {
    if (!lastOp) return FALLBACK_DIAGONAL;
    if (lastOp.capture.kind === "geometry-snapshot") {
      const { snapshot, objectId } = lastOp.capture;
      if (!objectId) return FALLBACK_DIAGONAL;
      const snapObj = commandSnapshotObject(snapshot, objectId);
      return snapObj ? objectDiagonal(snapObj) : FALLBACK_DIAGONAL;
    }
    return FALLBACK_DIAGONAL;
  }, [lastOp]);

  const dismiss = useCallback(() => {
    useStore.getState().clearLastOperation();
  }, []);
  const sel = usePanelSelection(PANEL_ID, dismiss);

  // Dismiss on outside click
  useEffect(() => {
    if (!lastOp) return;
    const handler = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest(`[data-draggable-panel="${PANEL_ID}"]`)) {
        dismiss();
      }
    };
    // Delay listener attachment to avoid dismissing on the confirm click itself
    const timer = setTimeout(() => {
      window.addEventListener("pointerdown", handler);
    }, 100);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("pointerdown", handler);
    };
  }, [lastOp, dismiss]);

  if (!lastOp) return null;

  const meta = getCommandMetadata(lastOp.type);
  if (meta.params.length === 0) return null;

  return (
    <DraggablePanel
      id={PANEL_ID}
      defaultPosition={{ bottom: 8, left: 8 }}
      zIndex={Z_FLOATING}
      style={{ width: 240, maxWidth: "calc(100vw - 16px)" }}
      bodyStyle={PANEL_BODY}
      header={
        <span
          style={{
            color: COLOR_ACCENT,
            fontSize: FONT_SIZE_BODY,
            fontWeight: 600,
          }}
        >
          {meta.label}
        </span>
      }
      {...sel}
    >
      <ParamList lastOp={lastOp} diagonal={diagonal} />
    </DraggablePanel>
  );
}

/**
 * Renders a `LastOperation`'s params, respecting `visibleWhen` predicates
 * and `paramGroups` foldouts. Ungrouped params render at the top of the
 * list; each group with at least one visible param renders as a
 * `FoldoutSection` followed by its params. Groups with no visible
 * params render no header at all (so the foldout cleanly disappears when its
 * gating condition is false).
 *
 * Foldout open/closed state is owned by the shared foldout primitive so the
 * after-op panel matches the rest of the inspector/sidebar section pattern.
 */
function ParamList({
  lastOp,
  diagonal,
}: {
  lastOp: NonNullable<ReturnType<typeof useStore.getState>["lastOperation"]>;
  diagonal: number;
}) {
  const meta = getCommandMetadata(lastOp.type);
  const groups = meta.paramGroups ?? [];

  const visible = meta.params.filter(
    (p) => !p.visibleWhen || p.visibleWhen(lastOp.params),
  );
  if (visible.length === 0) return null;

  const ungrouped = visible.filter((p) => !p.group);
  const groupParams = (groupKey: string) =>
    visible.filter((p) => p.group === groupKey);

  return (
    <>
      {ungrouped.map((paramDef) => (
        <ParamField
          key={paramDef.key}
          paramDef={paramDef}
          lastOp={lastOp}
          diagonal={diagonal}
        />
      ))}
      {groups.map((g) => {
        const params = groupParams(g.key);
        if (params.length === 0) return null;
        return (
          <div key={g.key} style={{ marginTop: 6 }}>
            <FoldoutSection
              id={`${PANEL_ID}:${lastOp.type}:${g.key}`}
              label={g.label}
              defaultCollapsed={!(g.defaultOpen ?? false)}
            >
              {params.map((paramDef) => (
                <ParamField
                  key={paramDef.key}
                  paramDef={paramDef}
                  lastOp={lastOp}
                  diagonal={diagonal}
                />
              ))}
            </FoldoutSection>
          </div>
        );
      })}
    </>
  );
}

function ParamField({
  paramDef,
  lastOp,
  diagonal,
}: {
  paramDef: ParamDef;
  lastOp: NonNullable<ReturnType<typeof useStore.getState>["lastOperation"]>;
  diagonal: number;
}) {
  const adjust = useStore.getState().adjustLastOperation;
  const value = lastOp.params[paramDef.key] ?? paramDef.default;

  const onChange = useCallback(
    (v: number | boolean | string) => {
      adjust({ ...lastOp.params, [paramDef.key]: v });
    },
    [adjust, lastOp.params, paramDef.key],
  );

  switch (paramDef.type) {
    case "float": {
      const range = resolveFloatRange(paramDef, diagonal);
      return (
        <SliderRow
          label={paramDef.label}
          value={value as number}
          min={range.min}
          max={range.max}
          step={range.step}
          displayValue={(value as number).toFixed(paramDef.precision ?? 2)}
          onChange={onChange as (v: number) => void}
          labelWidth={80}
          dimChromeOnDrag
        />
      );
    }
    case "int":
      return (
        <SliderRow
          label={paramDef.label}
          value={value as number}
          min={paramDef.min}
          max={paramDef.max}
          step={1}
          displayValue={String(value)}
          onChange={(v) => (onChange as (v: number) => void)(Math.round(v))}
          labelWidth={80}
          dimChromeOnDrag
        />
      );
    case "bool":
      return (
        <Toggle
          label={paramDef.label}
          checked={value as boolean}
          onChange={onChange as (v: boolean) => void}
        />
      );
    case "enum":
      return (
        <div style={{ marginTop: 6 }}>
          <span
            style={{
              fontSize: FONT_SIZE_SMALL,
              color: COLOR_TEXT_DIM,
              display: "block",
              marginBottom: 4,
            }}
          >
            {paramDef.label}
          </span>
          <SegmentedControl
            options={paramDef.options.map((o) => ({
              label: o.label,
              value: o.value,
            }))}
            value={value as string}
            onChange={onChange as (v: string) => void}
          />
        </div>
      );
  }
}
